import { useCallback, useMemo } from "react";
import { useApp } from "../app";
import { SubscriptionTiers, useUserInfo } from "../user";
const DEFAULT_MODEL_ID = "default";
// Helper function to normalize model level
const normalizeModelLevel = (model) => model.level
    ? model.level.toLowerCase()
    : SubscriptionTiers.FREE.toLowerCase();
/**
 * Groups models by their subscription tier and extracts the default model
 * @param models - Array of inference models to group
 * @returns Object containing grouped models and the default model
 */
const groupModelsByTier = (models) => {
    const grouped = {};
    let defaultModel = null;
    models.forEach((model) => {
        const level = normalizeModelLevel(model);
        if (model.id === DEFAULT_MODEL_ID) {
            defaultModel = model;
        }
        else {
            if (!grouped[level]) {
                grouped[level] = { models: [], highlightedModel: [] };
            }
            grouped[level].models.push(model);
            if (model.highlight) {
                grouped[level].highlightedModel.push(model);
            }
        }
    });
    return { grouped, defaultModel };
};
/**
 * Validates if a subscription tier is valid
 * @param tier - The subscription tier to validate
 * @returns True if the tier is valid, false otherwise
 */
const isValidSubscriptionTier = (tier) => {
    return tier !== null && tier !== undefined && tier.trim() !== "";
};
/**
 * Custom hook for managing AI model selection based on user subscription tiers
 * Provides tiered access to different AI inference models and handles premium model display
 * @returns Object containing getModelsByTier and getAvailableModels functions
 */
export const useModelsByTier = () => {
    const { subscriptionTier, permissions } = useUserInfo();
    const { inferenceModels = [] } = useApp();
    const availableModels = inferenceModels.reduce((acc, model) => {
        const lacksPermission = model.permission && !permissions?.includes(model.permission);
        const skipModel = model.hide_unauthorized && lacksPermission;
        if (skipModel) {
            return acc;
        }
        acc.push({
            ...model,
            isDisabled: Boolean(lacksPermission),
        });
        return acc;
    }, []);
    // Memoize the grouped models to avoid recalculating on every render
    const { grouped, defaultModel } = useMemo(() => {
        return groupModelsByTier(availableModels);
    }, [availableModels]);
    // Memoize tier order to avoid recreating array on every render
    const tierOrder = useMemo(() => [
        SubscriptionTiers.FREE,
        SubscriptionTiers.MINIMAL,
        SubscriptionTiers.BASIC,
        SubscriptionTiers.PREMIUM,
    ], []);
    // Get models grouped by tier for ModelSelectionHome
    const getModelsByTier = useCallback((activeModelId) => {
        if (!isValidSubscriptionTier(subscriptionTier)) {
            return { availableModels: [], premiumModels: [] };
        }
        const currentIndex = tierOrder.indexOf(subscriptionTier);
        if (currentIndex === -1) {
            return { availableModels: [], premiumModels: [] };
        }
        const currentTier = subscriptionTier.toLowerCase();
        let available = grouped[currentTier]?.models || [];
        const highlightedModels = [];
        for (let i = currentIndex; i >= 0; i--) {
            const tier = tierOrder[i];
            if (!tier) {
                continue;
            }
            const tierModels = grouped[tier.toLowerCase()]?.highlightedModel || [];
            highlightedModels.push(...tierModels);
        }
        if (activeModelId) {
            const active = availableModels.find((m) => m.id === activeModelId);
            const others = highlightedModels.filter((m) => m.id !== activeModelId);
            available = [active, ...others].filter((item) => !!item);
        }
        else {
            available = [...highlightedModels].filter((item) => !!item);
        }
        // Premium Models
        const premium = [];
        for (let i = currentIndex + 1; i <= tierOrder.length; i++) {
            const tier = tierOrder[i];
            if (!tier) {
                continue;
            }
            // Collect models available at this tier (and all lower tiers)
            const tierModels = grouped[tier.toLowerCase()]?.highlightedModel || [];
            premium.push(...tierModels);
        }
        return { availableModels: available, premiumModels: premium };
    }, [subscriptionTier, grouped, defaultModel, tierOrder]);
    // Get all available models for AllModelSelection
    const getAvailableModels = useCallback(() => {
        if (!isValidSubscriptionTier(subscriptionTier)) {
            return [];
        }
        const currentIndex = tierOrder.indexOf(subscriptionTier);
        if (currentIndex === -1) {
            return [];
        }
        const allAvailableModels = [];
        // Iterate tiers from highest → lowest to maintain proper order
        for (let i = currentIndex; i >= 0; i--) {
            const tier = tierOrder[i];
            if (!tier) {
                continue;
            }
            // Collect models available at this tier (and all lower tiers)
            const tierModels = (grouped[tier.toLowerCase()]?.models || []).filter((model) => !model.hide_unauthorized);
            allAvailableModels.push(...tierModels);
        }
        return [defaultModel, ...allAvailableModels].filter((model) => model !== null);
    }, [subscriptionTier, grouped, defaultModel, tierOrder]);
    return {
        getModelsByTier,
        getAvailableModels,
    };
};
